import type {
    ConfidenceReason,
    InferenceLevel,
    InferredColumn,
    SpreadsheetReadOptions,
    SearchPayload,
    Table,
    TableDataSearchResponse,
    OpenSearchListResponse,
    TableFieldsResponse,
    TableListResponse,
    TableRow
} from '../types/tables';
import { slugify } from '../utils/string';
import { APIClient } from 'vince-core/api/apiClient';
import { getBaseUrl } from 'vince-core/utils/env';
import { type Primitive, SortDirection } from 'vince-core/types/common';
import { inferSchema, toColumnType } from 'vince-core/utils/tables';
import { INFERENCE_PROBABLY_WRONG_THRESHOLD, INFERENCE_VERIFY_THRESHOLD } from 'vince-core/constants/tables';
import type { Query, QueryResponse } from 'vince-core/types/table-generic-search';

const baseUrl = getBaseUrl('/v1/custom-tables');

export async function getTablesList () {
    return await APIClient<TableListResponse>(`${baseUrl}/meta`);
}

export async function getTableDetails (tableName: string): Promise<Table> {
    return await APIClient<Table>(`${baseUrl}/meta/${tableName}`);
}

export async function findTableData (payload: SearchPayload): Promise<OpenSearchListResponse> {
    return await APIClient<OpenSearchListResponse>(`${baseUrl}/search/${payload.tableName}`, {
        method: 'POST',
        body: JSON.stringify(payload.body)
    });
}

export async function findTableDataGet (tableName: string, query: string, start: number, size: number, sortDirection: SortDirection, sortValue?: string): Promise<TableDataSearchResponse> {
    const q = new URLSearchParams({
        q: query,
        size: size + '',
        ...(sortValue ? { sort: sortValue } : {}),
        order: sortDirection.toString()
    });

    if (start > 0) {
        q.set('from', start + '');
    }

    return await APIClient<TableDataSearchResponse>(`${baseUrl}/search/${tableName}?${q.toString()}`);
}

export async function getTableFields (tableName: string): Promise<TableFieldsResponse> {
    const result = await APIClient<TableFieldsResponse>(`${baseUrl}/meta/${tableName}/fields`);

    result.items.forEach((field) => {
        if (!field.schemaType) {
            const { type, format } = inferSchema(field);
            field.schemaFormat = format;
            field.schemaType = type;
        }
    });

    return result;
}

export async function truncateTable (tableName: string) {
    await APIClient(`${baseUrl}/meta/${slugify(tableName)}/truncate`, { method: 'DELETE' });
}

export async function deleteTable (tableName: string) {
    await APIClient(`${baseUrl}/meta/${slugify(tableName)}`, { method: 'DELETE' });
}

export async function saveTable (table: Table, isEdit: boolean): Promise<Table> {
    if (isEdit) {
        return await APIClient<Table>(`${baseUrl}/meta/${table.tableName}`, {
            method: 'PATCH',
            body: table
        });
    } else {
        return await APIClient<Table>(`${baseUrl}/meta`, {
            method: 'POST',
            body: table
        });
    }
}

const withReadOptions = (url: string, options?: SpreadsheetReadOptions) => {
    const q = new URLSearchParams();
    if (options?.sheetName) {
        q.set('sheetName', options.sheetName);
    }
    if (options?.headerRow !== undefined) {
        q.set('headerRow', options.headerRow + '');
    }
    if (options?.dataRow !== undefined) {
        q.set('dataRow', options.dataRow + '');
    }
    const query = q.toString();
    return query ? `${url}?${query}` : url;
};

export async function uploadDataFromFile (tableName: string, excelFile: File, options?: SpreadsheetReadOptions) {
    const formData = new FormData();
    formData.append('excel', excelFile);

    const fetchOptions = {
        method: 'POST' as const,
        body: formData,
        silent: true
    };
    return await APIClient<{ rowsInserted: number, s3Key: string }>(withReadOptions(`${baseUrl}/data/${tableName}/file`, options), fetchOptions);
}

interface InferredSchemaResponse {
    type: 'object';
    properties?: Record<string, {
        type: string;
        format?: string;
        title?: string;
        'x-confidence'?: number;
        'x-confidence-reason'?: ConfidenceReason;
        'x-display-order'?: number;
        'x-primary-key'?: true;
    }>;
    'x-sampled-rows': number;
}

const CONFIDENCE_REASONS: string[] = ['empty', 'numeric-text', 'mixed-values', 'binary-integer', 'small-sample'];

const getInferenceLevel = (confidence: number, reason?: ConfidenceReason): InferenceLevel => {
    if (reason === 'empty') { return 'empty'; }
    // Most uploads are small, so flagging every column for it would be noise
    if (reason === 'small-sample' || confidence >= INFERENCE_VERIFY_THRESHOLD) { return 'ok'; }
    return confidence >= INFERENCE_PROBABLY_WRONG_THRESHOLD ? 'verify' : 'probablyWrong';
};

export async function inferSchemaFromFile (file: File, options?: SpreadsheetReadOptions): Promise<InferredColumn[]> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await APIClient<InferredSchemaResponse>(withReadOptions(`${baseUrl}/schema/infer/file`, options), {
        method: 'POST',
        body: formData
    });

    return Object.entries(response.properties ?? {})
        .sort(([, a], [, b]) => (a['x-display-order'] ?? 0) - (b['x-display-order'] ?? 0))
        .map(([name, property]) => {
            const { type, format } = toColumnType(property.type, property.format);
            const confidence = property['x-confidence'] ?? 0;
            const rawReason = property['x-confidence-reason'];
            const reason = rawReason && CONFIDENCE_REASONS.includes(rawReason) ? rawReason : undefined;
            return {
                name,
                title: property.title ?? name,
                type,
                format,
                primaryKey: property['x-primary-key'] === true,
                inference: {
                    confidence,
                    reason,
                    level: getInferenceLevel(confidence, reason)
                }
            };
        });
}

export async function deleteTableRows (tableName: string, rows: Partial<TableRow>[]) {
    return await APIClient(`${baseUrl}/data/${tableName}`, {
        method: 'DELETE',
        body: JSON.stringify(rows)
    });
}

export async function updateRow (tableName: string, body: Record<string, Primitive | undefined>) {
    return await APIClient(`${baseUrl}/data/${tableName}`, {
        method: 'PUT',
        body: JSON.stringify(body)
    });
}

export async function updatePartialRow (tableName: string, body: Record<string, Primitive | undefined>) {
    return await APIClient(`${baseUrl}/data/${tableName}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
    });
}

export async function genericSearch (tableName: string, query: Query) {
    return await APIClient<QueryResponse>(`${baseUrl}/generic-search/${tableName}`, {
        method: 'POST',
        body: JSON.stringify(query)
    });
}

